import { useEffect, useState } from 'react';
import { readTable, subscribeKey, writeTable } from 'utils/local-table';
import { ACTIVITY_WINDOW_MS } from './activity-window';

// Read-state = a set of notification ids; the notifications themselves come from the server.
export const READS_KEY = 'aktify_notification_reads_v1';

/**
 * A read mark plus when it was made. The timestamp exists only so the set can be
 * pruned — notification ids carry no date of their own. Rows written before this
 * shape existed are bare id strings; `normalize` upgrades them in place, so the
 * key is not versioned.
 */
interface ReadEntry {
  id: string;
  at: string;
}

/**
 * Coerce a stored row to a `ReadEntry`, or null if it carries no usable id.
 * A legacy bare string — or an entry whose `at` is missing/unparseable — is
 * stamped with `nowIso` rather than dropped: restamping costs one extra window
 * of retention, while dropping would silently resurface an already-read
 * notification as unread.
 */
const normalize = (row: unknown, nowIso: string): ReadEntry | null => {
  if (typeof row === 'string') return { id: row, at: nowIso };
  if (!row || typeof row !== 'object') return null;
  const { id, at } = row as Partial<ReadEntry>;
  if (typeof id !== 'string') return null;
  const usable = typeof at === 'string' && !Number.isNaN(Date.parse(at));
  return { id, at: usable ? (at as string) : nowIso };
};

export const readReadIds = (): string[] => {
  const nowIso = new Date().toISOString();
  return readTable<unknown>(READS_KEY)
    .map((row) => normalize(row, nowIso))
    .filter((entry): entry is ReadEntry => entry !== null)
    .map((entry) => entry.id);
};

/**
 * Union `ids` into the stored set, dropping marks older than the activity window
 * on the way through. Pruning on write is sufficient to bound the set: writes are
 * the only thing that grows it, so no timer or periodic sweep is needed.
 */
export const addReadIds = (ids: string[]): void => {
  if (ids.length === 0) return;
  const now = Date.now();
  const nowIso = new Date(now).toISOString();
  const cutoff = now - ACTIVITY_WINDOW_MS;

  // Keyed by id so duplicates collapse and a re-mark refreshes the timestamp.
  const byId = new Map<string, ReadEntry>();
  for (const row of readTable<unknown>(READS_KEY)) {
    const entry = normalize(row, nowIso);
    if (entry && Date.parse(entry.at) >= cutoff) byId.set(entry.id, entry);
  }
  for (const id of ids) byId.set(id, { id, at: nowIso });
  writeTable(READS_KEY, [...byId.values()]);
};

const sameIds = (a: Set<string>, b: Set<string>): boolean =>
  a.size === b.size && [...a].every((id) => b.has(id));

// Reactive read-id set — re-reads on same-tab/cross-tab writes.
// Keeps the previous Set when the contents are unchanged: this hook is mounted once per action card,
// so returning a fresh identity on every write would re-render every card on the page (each of which
// re-scans the activity feed) whenever any one of them marked something read.
export const useReadIds = (): Set<string> => {
  const [ids, setIds] = useState<Set<string>>(() => new Set(readReadIds()));
  useEffect(
    () =>
      subscribeKey(READS_KEY, () =>
        setIds((prev) => {
          const next = new Set(readReadIds());
          return sameIds(prev, next) ? prev : next;
        }),
      ),
    [],
  );
  return ids;
};
